import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer, Modal, message } from "antd";
import type { CreateStoreOrderResult } from "../../customer_account/StoreOrderType";
import { StoreOrderApi } from "../../customer_account/StoreOrderApi";
import { resolveH5StoreOrderDetailPath } from "../../customer_account/storeOrderDetailUtils";
import {
  isDuplicateUnpaidCreateResult,
  promptDuplicateUnpaidOrder,
} from "../../customer_account/storeOrderDuplicateUnpaidPrompt";
import { h5NavigateTo } from "../../utils/miniProgramEnv";
import { isDouyinMiniProgramSubscribeBlocked, promptDouyinMiniProgramSubscribeBlocked } from "../../utils/douyinMiniProgramSubscribeGuard";
import type { MembershipProduct } from "../SpaceType";
import { enabledStrategySpecs, pickDefaultStrategyId } from "../spaceProductPurchaseUtils";
import { membershipNoRetailPurchaseHint } from "../spacePageUtils";
import { spaceProductPurchaseSheetCssVars } from "./SpaceProductSubscribeConfig";
import "./SpaceProductPurchaseSheet.css";

export type SpaceProductPurchaseSheetProps = {
  spaceId: string;
  row: MembershipProduct;
  open: boolean;
  onClose: () => void;
  isLoggedIn: boolean;
  onLoginRequest: () => void;
};

export function SpaceProductPurchaseSheet(props: SpaceProductPurchaseSheetProps) {
  const { spaceId, row, open, onClose, isLoggedIn, onLoginRequest } = props;
  const navigate = useNavigate();
  const isStrategyTool = row.productKind === "strategy_tool";
  const isProductBundle = row.productKind === "product_bundle";
  const isMarket = isStrategyTool && row.strategySaleMode === "market";
  const isRetail = row.pricingMode?.trim().toLowerCase() === "retail";
  const bundleSpecs = useMemo(
    () => (row.priceSpecs ?? []).filter((s) => s.enabled),
    [row.priceSpecs]
  );
  const retailSpecs = isRetail ? row.priceSpecs ?? [] : [];
  const offers = useMemo(() => row.strategySkuOffers ?? [], [row.strategySkuOffers]);

  const [straId, setStraId] = useState<string | null>(null);
  const [tierId, setTierId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const specs = isMarket
    ? enabledStrategySpecs(offers, straId)
    : isStrategyTool
      ? bundleSpecs
      : retailSpecs.filter((s) => s.enabled);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (isMarket) {
      const entitled =
        row.strategyEntitlements?.filter((e) => e.active).map((e) => e.subProductItemId) ?? [];
      const pick = pickDefaultStrategyId(offers, undefined, entitled);
      setStraId(pick);
      const nextSpecs = enabledStrategySpecs(offers, pick);
      setTierId(nextSpecs[0]?.tierId ?? null);
      return;
    }
    setStraId(null);
    const list = isStrategyTool ? bundleSpecs : retailSpecs.filter((s) => s.enabled);
    setTierId(list.find((s) => s.enabled)?.tierId ?? list[0]?.tierId ?? null);
  }, [open, isMarket, isStrategyTool, offers, bundleSpecs, retailSpecs, row.strategyEntitlements]);

  const maybeSignAndCreate = useCallback(async (
    forceCreate = false
  ): Promise<CreateStoreOrderResult | string> => {
    const tier = tierId ?? specs[0]?.tierId;
    if (!tier) {
      return "请选择规格";
    }
    if (isMarket && !straId) {
      return "请选择策略";
    }
    const base = {
      spaceId,
      spaceProductId: row.spaceProductId,
      serviceTier: tier,
      subProductItemId: isMarket ? straId ?? undefined : undefined,
      forceCreate: forceCreate || undefined,
    };
    let res = await StoreOrderApi.createOrder(base);
    if (typeof res !== "string" && res.createResult === 401) {
      const needSign = res;
      const tid = row.agreementTemplateId?.trim();
      if (!tid) {
        message.error(needSign.failMessage ?? "请先签署协议");
        return needSign;
      }
      await new Promise<void>((resolve) => {
        Modal.confirm({
          title: "签署购买协议",
          content: needSign.failMessage ?? "需先签署产品购买协议",
          okText: "去签署",
          cancelText: "取消",
          width: 360,
          centered: true,
          onOk: async () => {
            const signRes = await StoreOrderApi.signAgreement({
              spaceId,
              spaceProductId: row.spaceProductId,
              templateId: tid,
            });
            if (typeof signRes === "string") {
              message.error(signRes);
            } else {
              message.success("签署成功");
            }
            resolve();
          },
          onCancel: () => resolve(),
        });
      });
      res = await StoreOrderApi.createOrder({
        ...base,
      });
    }
    return res;
  }, [isMarket, row, spaceId, specs, straId, tierId]);

  const confirmPurchase = async () => {
    if (!isLoggedIn) {
      onLoginRequest();
      return;
    }
    if (isDouyinMiniProgramSubscribeBlocked()) {
      promptDouyinMiniProgramSubscribeBlocked();
      return;
    }
    if (row.purchaseBlocked) {
      message.info(membershipNoRetailPurchaseHint(row));
      return;
    }
    if (isMarket && !straId) {
      message.warning("请先选择策略");
      return;
    }
    if (specs.length > 0 && !tierId) {
      message.warning("请先选择规格");
      return;
    }
    setPending(true);
    try {
      let res = await maybeSignAndCreate(false);
      if (typeof res === "string") {
        if (res !== "请选择规格" && res !== "请选择策略") {
          message.error(res);
        }
        return;
      }
      if (isDuplicateUnpaidCreateResult(res)) {
        const action = await promptDuplicateUnpaidOrder(res.failMessage ?? undefined);
        if (action === "pay") {
          onClose();
          h5NavigateTo(navigate, resolveH5StoreOrderDetailPath(spaceId, res.existingOrderId));
          return;
        }
        res = await maybeSignAndCreate(true);
        if (typeof res === "string") {
          if (res !== "请选择规格" && res !== "请选择策略") {
            message.error(res);
          }
          return;
        }
      }
      if (res.createResult !== 0 || !res.orderId) {
        message.error(res.failMessage ?? "下单失败");
        return;
      }
      onClose();
      h5NavigateTo(navigate, resolveH5StoreOrderDetailPath(spaceId, res.orderId));
    } finally {
      setPending(false);
    }
  };

  const showStrategyRow = isMarket && offers.length > 0;
  const showSpecRow = specs.length > 0 && (!isMarket || straId != null);
  const leadText = row.userSubscribed
    ? "您目前已经在订阅状态，延长权益周期，保持不间断会员权益"
    : isProductBundle
      ? "开通后将获得打包产品权益，可按套餐访问所含全部产品"
      : "开通后可按权益使用本空间会员服务";

  return (
    <Drawer
      placement="bottom"
      open={open}
      onClose={onClose}
      height="auto"
      className="space-purchase-sheet"
      destroyOnClose
      closable={false}
      styles={{ body: { padding: 0 } }}
    >
      <div className="space-purchase-sheet__inner" style={spaceProductPurchaseSheetCssVars()}>
        <header className="space-purchase-sheet__header">
          <p className="space-purchase-sheet__title">{row.displayName}</p>
          <button type="button" className="space-purchase-sheet__close" onClick={onClose}>
            关闭
          </button>
        </header>
        <section className="space-purchase-sheet__intro">
          {row.description?.trim() ? (
            <p>{row.description.trim()}</p>
          ) : (
            <p className="space-purchase-sheet__intro-placeholder">暂无详细介绍</p>
          )}
        </section>
        {showStrategyRow ? (
          <section className="space-purchase-sheet__section">
            <p className="space-purchase-sheet__label">选择策略</p>
            <div className="space-purchase-sheet__grid">
              {offers.map((o) => {
                const active = straId === o.subProductItemId;
                return (
                  <button
                    key={o.subProductItemId}
                    type="button"
                    className={
                      active
                        ? "space-purchase-sheet__tier space-purchase-sheet__tier--active"
                        : "space-purchase-sheet__tier"
                    }
                    onClick={() => {
                      setStraId(o.subProductItemId);
                      const next = enabledStrategySpecs(offers, o.subProductItemId);
                      setTierId(next[0]?.tierId ?? null);
                    }}
                  >
                    {o.name}
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}
        {showSpecRow ? (
          <section className="space-purchase-sheet__section">
            <p className="space-purchase-sheet__label">选择规格</p>
            <div className="space-purchase-sheet__grid">
              {specs.map((s) => {
                const active = tierId === s.tierId;
                return (
                  <button
                    key={s.tierId}
                    type="button"
                    className={
                      active
                        ? "space-purchase-sheet__tier space-purchase-sheet__tier--active"
                        : "space-purchase-sheet__tier"
                    }
                    onClick={() => setTierId(s.tierId)}
                  >
                    <span>{s.specName || s.durationLabel}</span>
                    <span className="space-purchase-sheet__price">¥{s.priceYuan}</span>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}
        <p className="space-purchase-sheet__lead">{leadText}</p>
        <div className="space-purchase-sheet__foot">
          <button type="button" className="space-purchase-sheet__btn space-purchase-sheet__btn--cancel" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="space-purchase-sheet__btn space-purchase-sheet__btn--primary"
            disabled={pending}
            onClick={() => void confirmPurchase()}
          >
            {pending ? "处理中…" : "立即订阅"}
          </button>
        </div>
      </div>
    </Drawer>
  );
}
